import React from 'react';

interface DateCalendarProps {
  selectedDate: string | null;
  datesWithRunStarts: string[]; // Dates where runs start
  datesWithData: string[]; // Dates that fall within any run's time range
  onDateSelect: (date: string) => void;
  onMonthChange?: (monthStr: string) => void; 
  currentMonth?: string; // YYYY-MM format
}

const DateCalendar: React.FC<DateCalendarProps> = ({
  selectedDate,
  datesWithRunStarts,
  datesWithData,
  onDateSelect,
  onMonthChange,
  currentMonth,
}) => {
  const getMonthYear = () => {
    if (currentMonth) {
      const [year, month] = currentMonth.split('-');
      return { year: parseInt(year), month: parseInt(month) };
    }
    if (selectedDate) {
      try {
        const date = new Date(selectedDate + 'T00:00:00');
        if (!isNaN(date.getTime())) {
          return { year: date.getFullYear(), month: date.getMonth() + 1 };
        }
      } catch (e) {
      }
    }
    // Default to current month
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  };

  const { year, month } = getMonthYear();

  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();

  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  const formatDate = (day: number): string => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return dateStr;
  };

  const hasRunStart = (day: number): boolean => {
    const dateStr = formatDate(day);
    return datesWithRunStarts.includes(dateStr);
  };

  const isDateWithData = (day: number): boolean => {
    const dateStr = formatDate(day);
    return datesWithData.includes(dateStr);
  };

  const isSelected = (day: number): boolean => {
    if (!selectedDate) return false;
    const dateStr = formatDate(day);
    return dateStr === selectedDate;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const changeMonth = (delta: number) => {
    const newDate = new Date(year, month - 1 + delta, 1);
    const monthStr = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`;
    if (onMonthChange) {
      onMonthChange(monthStr);
    } else {
      const newDateStr = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-01`;
      onDateSelect(newDateStr);
    }
  };

  return (
    <div style={{ 
      border: '1px solid #dee2e6', 
      borderRadius: '8px', 
      padding: '1rem',
      background: '#ffffff',
      minWidth: '300px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <button
          onClick={() => changeMonth(-1)}
          style={{
            padding: '0.5rem',
            background: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          ‹
        </button>
        <h4 style={{ margin: 0 }}>
          {monthNames[month - 1]} {year}
        </h4>
        <button
          onClick={() => changeMonth(1)}
          style={{
            padding: '0.5rem',
            background: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          ›
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.25rem' }}>
        {/* Day headers */}
        {dayNames.map((day) => (
          <div
            key={day}
            style={{
              padding: '0.5rem',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '0.85rem',
              color: '#666',
            }}
          >
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {days.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} style={{ padding: '0.5rem' }} />;
          }

          const dateStr = formatDate(day);
          const runStarts = hasRunStart(day);
          const hasData = isDateWithData(day);
          const selected = isSelected(day);

          // Run start takes precedence over has data
          const indicatorColor = runStarts ? '#ffc107' : (hasData ? '#28a745' : null);
          const indicatorTitle = runStarts 
            ? `Run Starting Date: ${dateStr}` 
            : (hasData ? `Has data: ${dateStr}` : dateStr);

          return (
            <button
              key={day}
              onClick={() => onDateSelect(dateStr)}
              style={{
                padding: '0.5rem',
                border: selected ? '2px solid #007bff' : '1px solid #dee2e6',
                borderRadius: '4px',
                background: selected
                  ? '#e7f3ff'
                  : runStarts
                  ? '#fff3cd'
                  : hasData
                  ? '#d4edda'
                  : '#ffffff',
                cursor: 'pointer',
                fontWeight: selected ? 'bold' : 'normal',
                color: selected ? '#007bff' : runStarts ? '#856404' : (hasData ? '#155724' : '#000'),
                position: 'relative',
              }}
              title={indicatorTitle}
            >
              {day}
              {indicatorColor && !selected && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: '2px',
                    right: '2px',
                    width: '6px',
                    height: '6px',
                    background: indicatorColor,
                    borderRadius: '50%',
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#666', display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '12px', height: '12px', background: '#ffc107', borderRadius: '50%' }} />
          <span>Run Starting Date</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '12px', height: '12px', background: '#28a745', borderRadius: '50%' }} />
          <span>Has data</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '12px', height: '12px', background: '#e7f3ff', border: '2px solid #007bff', borderRadius: '2px' }} />
          <span>Selected</span>
        </div>
      </div>
    </div>
  );
};

export default DateCalendar;

